import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = '';

const QUICK_QUESTIONS = [
  '증권 발행의 주요 요건은 무엇인가요?',
  '공시 의무 사항에 대해 알려주세요.',
  '규정 위반 시 제재 조치는 어떻게 되나요?',
  '이 규정의 목적과 적용 범위는?',
  '전환사채 발행 제한에 대해 설명해주세요.',
  '자기주식 취득 절차와 방법은?',
];

/* ---- TTS 음성 지원 ---- */
function useTTS() {
  const [speaking, setSpeaking] = useState(false);
  const [supported] = useState(() => 'speechSynthesis' in window);
  const utterRef = useRef(null);

  const speak = (text) => {
    if (!supported) return alert('이 브라우저는 음성 지원을 지원하지 않습니다.');
    window.speechSynthesis.cancel();
    const plain = text.replace(/[#*|_>`~[\]]/g, '').replace(/\n+/g, '. ');
    const utter = new SpeechSynthesisUtterance(plain);
    utter.lang = 'ko-KR';
    utter.rate = 1.0;
    utter.pitch = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const koVoice = voices.find(v => v.lang.startsWith('ko'));
    if (koVoice) utter.voice = koVoice;
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    utterRef.current = utter;
    setSpeaking(true);
    window.speechSynthesis.speak(utter);
  };

  const stop = () => {
    window.speechSynthesis.cancel();
    setSpeaking(false);
  };

  return { speak, stop, speaking, supported };
}

/* ---- 메일 모달 ---- */
function MailModal({ show, onClose, question, answer }) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('[업무챗봇] 법령 상담 결과');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState('');

  if (!show) return null;

  const handleSend = async () => {
    if (!to.trim()) return alert('수신자 이메일을 입력해주세요.');
    setSending(true);
    setResult('');
    try {
      const res = await fetch(`${API}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, subject, question, answer })
      });
      const data = await res.json();
      setResult(data.error ? 'error:' + data.error : 'success:' + data.message);
    } catch (e) {
      setResult('error:전송 실패');
    }
    setSending(false);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'white', borderRadius: 12, width: '90%', maxWidth: 480, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ background: 'linear-gradient(135deg, #e67700, #f4a523)', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: 'white', fontSize: 17, fontWeight: 'bold' }}>답변 메일로 공유</span>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', fontSize: 15 }}>X</button>
        </div>
        <div style={{ padding: 22 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 'bold', display: 'block', marginBottom: 4 }}>받는 사람</label>
            <input type="text" value={to} onChange={e => setTo(e.target.value)}
              placeholder="example@email.com (여러 명은 쉼표로 구분)"
              style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 'bold', display: 'block', marginBottom: 4 }}>제목</label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div style={{ background: '#f8f9fa', borderRadius: 6, padding: 12, marginBottom: 14, maxHeight: 120, overflowY: 'auto', fontSize: 12, border: '1px solid #eee', color: '#555', whiteSpace: 'pre-wrap' }}>
            <div style={{ fontWeight: 'bold', color: '#e67700', marginBottom: 4 }}>Q: {question}</div>
            {answer.substring(0, 300)}{answer.length > 300 ? '...' : ''}
          </div>
          {result && (
            <div style={{
              padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13,
              background: result.startsWith('success') ? '#e8f5e9' : '#ffebee',
              color: result.startsWith('success') ? '#2e7d32' : '#c62828',
            }}>{result.split(':').slice(1).join(':')}</div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '9px 18px', background: '#f1f3f4', color: '#333', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>취소</button>
            <button onClick={handleSend} disabled={sending}
              style={{ padding: '9px 22px', background: sending ? '#ccc' : '#e67700', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 'bold' }}>
              {sending ? '전송중...' : '메일 보내기'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- 액션 버튼 바 ---- */
function ActionBar({ content, question, onSpeak, onStopSpeak, isSpeaking, ttsSupported }) {
  const [showMail, setShowMail] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 4, marginTop: 10, flexWrap: 'wrap' }}>
        {/* 음성 듣기 */}
        {ttsSupported && (
          <button onClick={() => isSpeaking ? onStopSpeak() : onSpeak(content)}
            title={isSpeaking ? '음성 중지' : '음성으로 듣기'}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '5px 10px', background: isSpeaking ? '#c62828' : '#f5f5f5',
              color: isSpeaking ? 'white' : '#555',
              border: '1px solid ' + (isSpeaking ? '#c62828' : '#ddd'), borderRadius: 16,
              cursor: 'pointer', fontSize: 12,
            }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isSpeaking ? 'white' : '#555'} strokeWidth="2">
              {isSpeaking ? (
                <><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></>
              ) : (
                <><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></>
              )}
            </svg>
            {isSpeaking ? '중지' : '음성'}
          </button>
        )}
        {/* 복사 */}
        <button onClick={handleCopy} title="답변 복사"
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '5px 10px', background: copied ? '#e8f5e9' : '#f5f5f5',
            color: copied ? '#2e7d32' : '#555',
            border: '1px solid ' + (copied ? '#a5d6a7' : '#ddd'), borderRadius: 16,
            cursor: 'pointer', fontSize: 12,
          }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={copied ? '#2e7d32' : '#555'} strokeWidth="2">
            {copied ? (
              <path d="M20 6L9 17l-5-5"/>
            ) : (
              <><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>
            )}
          </svg>
          {copied ? '복사됨' : '복사'}
        </button>
        {/* 메일 */}
        <button onClick={() => setShowMail(true)} title="메일로 공유"
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '5px 10px', background: '#f5f5f5', color: '#555',
            border: '1px solid #ddd', borderRadius: 16,
            cursor: 'pointer', fontSize: 12,
          }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2">
            <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/>
          </svg>
          메일
        </button>
      </div>
      <MailModal show={showMail} onClose={() => setShowMail(false)} question={question} answer={content} />
    </>
  );
}

/* ---- 메인 챗봇 ---- */
function ChatbotAgent() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [lawInfo, setLawInfo] = useState(null);
  const [fontSize, setFontSize] = useState(14);
  const chatEndRef = useRef(null);
  const tts = useTTS();

  const zoomIn = () => setFontSize(s => Math.min(s + 2, 28));
  const zoomOut = () => setFontSize(s => Math.max(s - 2, 12));
  const zoomReset = () => setFontSize(14);

  useEffect(() => {
    fetch(`${API}/chatbot/laws`).then(r => r.json()).then(d => setLawInfo(d)).catch(() => {});
    // Preload voices
    if ('speechSynthesis' in window) window.speechSynthesis.getVoices();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const q = text || input;
    if (!q.trim() || loading) return;
    setInput('');
    tts.stop();

    const userMsg = { role: 'user', content: q };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      const res = await fetch(`${API}/chatbot/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, history: newMessages.slice(-10) })
      });
      const data = await res.json();
      setMessages([...newMessages, { role: 'assistant', content: data.answer || data.error }]);
    } catch (e) {
      setMessages([...newMessages, { role: 'assistant', content: '응답을 받지 못했습니다.' }]);
    }
    setLoading(false);
  };

  // Find question for a given assistant message index
  const getQuestionFor = (idx) => {
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return messages[i].content;
    }
    return '';
  };

  const mdComponents = {
    h1: ({children}) => <h1 style={{ marginTop: 14, marginBottom: 6, fontSize: 17, color: '#e67700', borderBottom: '2px solid #fef3e2', paddingBottom: 4 }}>{children}</h1>,
    h2: ({children}) => <h2 style={{ marginTop: 14, marginBottom: 6, fontSize: 16, color: '#e67700' }}>{children}</h2>,
    h3: ({children}) => <h3 style={{ marginTop: 12, marginBottom: 6, fontSize: 15, color: '#e67700', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 4, height: 16, background: '#e67700', borderRadius: 2, display: 'inline-block' }}/>{children}
    </h3>,
    strong: ({children}) => <strong style={{ color: '#b45309', background: '#fef9ee', padding: '1px 4px', borderRadius: 3 }}>{children}</strong>,
    ul: ({children}) => <ul style={{ paddingLeft: 18, margin: '6px 0' }}>{children}</ul>,
    ol: ({children}) => <ol style={{ paddingLeft: 18, margin: '6px 0' }}>{children}</ol>,
    li: ({children}) => <li style={{ marginBottom: 4, lineHeight: 1.6 }}>{children}</li>,
    p: ({children}) => <p style={{ margin: '7px 0', lineHeight: 1.7 }}>{children}</p>,
    blockquote: ({children}) => <blockquote style={{ margin: '8px 0', padding: '8px 14px', borderLeft: '3px solid #e67700', background: '#fffbf0', borderRadius: '0 6px 6px 0', fontSize: 13, color: '#8a5a00' }}>{children}</blockquote>,
    code: ({children}) => <code style={{ background: '#f5f0e8', padding: '2px 6px', borderRadius: 4, fontSize: 13, color: '#8a5a00' }}>{children}</code>,
    table: ({children}) => <div style={{ overflowX: 'auto', margin: '8px 0' }}><table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>{children}</table></div>,
    th: ({children}) => <th style={{ border: '1px solid #f0d9a8', padding: '7px 10px', background: '#fef3e2', textAlign: 'left', fontWeight: 'bold', color: '#8a5a00' }}>{children}</th>,
    td: ({children}) => <td style={{ border: '1px solid #f0d9a8', padding: '7px 10px' }}>{children}</td>,
    hr: () => <hr style={{ border: 'none', borderTop: '1px dashed #e0d0b0', margin: '12px 0' }} />,
  };

  return (
    <div style={{ minHeight: '100vh', background: '#faf8f5', display: 'flex', flexDirection: 'column' }}>
      {/* 헤더 */}
      <div style={{
        background: 'linear-gradient(135deg, #8a5a00 0%, #e67700 50%, #f4a523 100%)',
        padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16,
        boxShadow: '0 2px 12px rgba(230,119,0,0.3)'
      }}>
        <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', width: 36, height: 36, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12 4l-6 6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ color: 'white', fontSize: 18, fontWeight: 'bold' }}>업무 챗봇</div>
          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>
            증권의 발행 및 공시 등에 관한 규정
            {lawInfo && <span> | {lawInfo.article_count}개 조문 학습</span>}
          </div>
        </div>
        {/* 접근성 도구 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* 돋보기 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, background: 'rgba(255,255,255,0.15)', borderRadius: 12, padding: '2px 4px' }}>
            <button onClick={zoomOut} title="글자 축소"
              style={{ width: 28, height: 28, border: 'none', background: 'transparent', color: 'white', cursor: 'pointer', fontSize: 16, fontWeight: 'bold', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/>
              </svg>
            </button>
            <button onClick={zoomReset} title="기본 크기로"
              style={{ minWidth: 32, height: 28, border: 'none', background: 'transparent', color: 'white', cursor: 'pointer', fontSize: 11, fontWeight: 'bold', borderRadius: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {fontSize}
            </button>
            <button onClick={zoomIn} title="글자 확대"
              style={{ width: 28, height: 28, border: 'none', background: 'transparent', color: 'white', cursor: 'pointer', fontSize: 16, fontWeight: 'bold', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
              </svg>
            </button>
          </div>
          {/* 음성 지원 뱃지 */}
          {tts.supported && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.15)', padding: '4px 10px', borderRadius: 12 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
              <span style={{ color: 'white', fontSize: 11 }}>음성</span>
            </div>
          )}
        </div>
      </div>

      {/* 채팅 영역 */}
      <div style={{ flex: 1, maxWidth: 800, width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', padding: '0 20px' }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 0' }}>

          {/* 시작 화면 */}
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <div style={{ width: 72, height: 72, borderRadius: 20, background: 'linear-gradient(135deg, #fef3e2, #fff8ee)', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(230,119,0,0.15)' }}>
                <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
                  <rect x="4" y="8" width="28" height="20" rx="4" fill="#fef3e2" stroke="#e67700" strokeWidth="2"/>
                  <rect x="16" y="20" width="28" height="20" rx="4" fill="#fef3e2" stroke="#e67700" strokeWidth="2"/>
                  <circle cx="24" cy="30" r="2" fill="#e67700"/><circle cx="30" cy="30" r="2" fill="#e67700"/><circle cx="36" cy="30" r="2" fill="#e67700"/>
                </svg>
              </div>
              <h3 style={{ color: '#333', marginBottom: 6, fontSize: 18 }}>증권 발행 및 공시 규정 상담</h3>
              <p style={{ color: '#999', fontSize: 14, marginBottom: 6 }}>법령 전문을 학습한 AI가 정확한 조문을 근거로 답변합니다.</p>
              {tts.supported && <p style={{ color: '#b45309', fontSize: 13, marginBottom: 24 }}>답변의 음성 듣기, 복사, 메일 공유가 가능합니다.</p>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, maxWidth: 600, margin: '0 auto' }}>
                {QUICK_QUESTIONS.map((q, i) => (
                  <button key={i} onClick={() => sendMessage(q)}
                    style={{
                      padding: '12px 14px', background: 'white', border: '1px solid #f0d9a8',
                      borderRadius: 12, cursor: 'pointer', fontSize: Math.max(fontSize - 1, 13), color: '#8a5a00',
                      textAlign: 'left', lineHeight: 1.4, transition: 'all 0.15s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#e67700'; e.currentTarget.style.background = '#fffbf0'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#f0d9a8'; e.currentTarget.style.background = 'white'; }}
                  >{q}</button>
                ))}
              </div>
            </div>
          )}

          {/* 메시지들 */}
          {messages.map((msg, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: msg.role === 'assistant' ? 20 : 12,
            }}>
              {msg.role === 'assistant' && (
                <div style={{ width: 32, height: 32, borderRadius: 10, background: '#fef3e2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10, flexShrink: 0, marginTop: 2 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#fef3e2" stroke="#e67700" strokeWidth="1.5"/>
                    <circle cx="9" cy="10" r="1.5" fill="#e67700"/><circle cx="15" cy="10" r="1.5" fill="#e67700"/>
                    <path d="M8 15c1 1.5 3 2.5 4 2.5s3-1 4-2.5" stroke="#e67700" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
              )}
              <div style={{ maxWidth: msg.role === 'user' ? '75%' : '85%', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  padding: msg.role === 'user' ? '10px 16px' : '16px 20px',
                  borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '2px 18px 18px 18px',
                  background: msg.role === 'user'
                    ? 'linear-gradient(135deg, #e67700, #f09819)'
                    : 'white',
                  color: msg.role === 'user' ? 'white' : '#333',
                  border: msg.role === 'user' ? 'none' : '1px solid #f0e4d0',
                  fontSize: fontSize, lineHeight: 1.7,
                  boxShadow: msg.role === 'user'
                    ? '0 2px 8px rgba(230,119,0,0.25)'
                    : '0 1px 6px rgba(0,0,0,0.06)',
                }}>
                  {msg.role === 'user' ? msg.content : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{msg.content}</ReactMarkdown>
                  )}
                </div>
                {msg.role === 'assistant' && (
                  <ActionBar
                    content={msg.content}
                    question={getQuestionFor(i)}
                    onSpeak={tts.speak}
                    onStopSpeak={tts.stop}
                    isSpeaking={tts.speaking}
                    ttsSupported={tts.supported}
                  />
                )}
              </div>
            </div>
          ))}

          {/* 로딩 */}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 16 }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: '#fef3e2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10, flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#fef3e2" stroke="#e67700" strokeWidth="1.5"/>
                  <circle cx="9" cy="10" r="1.5" fill="#e67700"/><circle cx="15" cy="10" r="1.5" fill="#e67700"/>
                  <path d="M8 15c1 1.5 3 2.5 4 2.5s3-1 4-2.5" stroke="#e67700" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={{
                padding: '14px 20px', borderRadius: '2px 18px 18px 18px',
                background: 'white', border: '1px solid #f0e4d0',
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
              }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[0,1,2].map(n => (
                    <div key={n} style={{
                      width: 8, height: 8, borderRadius: '50%', background: '#e67700',
                      animation: `bounce 1.2s ${n * 0.2}s infinite ease-in-out`,
                    }}/>
                  ))}
                </div>
                <span style={{ color: '#b45309', fontSize: 13 }}>답변 작성 중...</span>
              </div>
              <style>{`@keyframes bounce { 0%,80%,100% { transform: translateY(0); opacity: 0.4; } 40% { transform: translateY(-8px); opacity: 1; } }`}</style>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* 입력 영역 */}
        <div style={{
          padding: '12px 0 20px', borderTop: '1px solid #f0e4d0',
          display: 'flex', gap: 10, background: '#faf8f5'
        }}>
          <input type="text" value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !loading) sendMessage(); }}
            placeholder="규정에 대해 질문하세요..."
            style={{
              flex: 1, padding: '12px 18px', borderRadius: 24, border: '1px solid #e0d4c0',
              fontSize: Math.max(fontSize, 15), outline: 'none', background: 'white',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}
            onFocus={e => e.target.style.borderColor = '#e67700'}
            onBlur={e => e.target.style.borderColor = '#e0d4c0'}
          />
          <button onClick={() => sendMessage()} disabled={loading}
            style={{
              width: 48, height: 48, borderRadius: '50%', border: 'none',
              background: loading ? '#ddd' : 'linear-gradient(135deg, #e67700, #f09819)',
              color: 'white', cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: loading ? 'none' : '0 2px 8px rgba(230,119,0,0.3)',
            }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatbotAgent;
